import React, { useCallback, useEffect, useState } from 'react';
import config from '../config/config';
import teamMembers from '../data/teamMembers';

const getMemberPhoto = (name) => config.teamPhotos?.[name] || '';

const Avatar = ({ member, sizeClass, ringClass, textClass }) => {
    const photoUrl = getMemberPhoto(member.name);

    if (photoUrl) {
        return (
            <img
                src={photoUrl}
                alt={member.name}
                loading="lazy"
                decoding="async"
                className={`${sizeClass} rounded-full object-cover object-top ${ringClass}`}
            />
        );
    }

    return (
        <div className={`flex items-center justify-center rounded-full bg-gradient-to-br ${member.avatarColor} font-medium text-white ${sizeClass} ${ringClass} ${textClass}`}>
            {member.name.charAt(0)}
        </div>
    );
};

const MemberSlide = ({ member }) => {
    const isPortrait = !member.description;

    if (isPortrait) {
        return (
            <div className="w-full shrink-0 px-2">
                <div className="mx-auto flex min-h-[232px] max-w-3xl flex-col items-center justify-center rounded-3xl border border-gray-100 bg-gradient-to-b from-violet-50/70 via-white to-white px-6 py-8 shadow-sm md:min-h-[248px] md:py-10">
                    <div className="relative">
                        <div
                            className="absolute -inset-3 rounded-full bg-gradient-to-br from-violet-400/25 to-purple-500/10 blur-md"
                            aria-hidden
                        />
                        <Avatar
                            member={member}
                            sizeClass="relative h-32 w-32 md:h-40 md:w-40"
                            ringClass="ring-4 ring-white shadow-lg"
                            textClass="text-4xl"
                        />
                    </div>
                    <h3 className="mt-5 text-xl font-bold tracking-wide text-gray-900">{member.name}</h3>
                    <p className="mt-1.5 text-sm font-medium text-blue-600">{member.position}</p>
                    {member.department ? (
                        <p className="mt-1 text-xs tracking-wide text-gray-400">{member.department}</p>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full shrink-0 px-2">
            <div className="mx-auto flex min-h-[232px] max-w-3xl flex-col items-center gap-5 rounded-3xl border border-gray-100 bg-gradient-to-b from-gray-50/60 to-white p-6 shadow-sm md:min-h-[248px] md:flex-row md:items-start md:gap-7 md:p-8">
                <div className="shrink-0">
                    <Avatar
                        member={member}
                        sizeClass="h-24 w-24 md:h-28 md:w-28"
                        ringClass="ring-4 ring-white shadow-md"
                        textClass="text-3xl"
                    />
                </div>
                <div className="min-w-0 flex-1 text-center md:text-left">
                    <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-1 md:justify-start">
                        <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                        <span className="text-sm font-medium text-blue-600">{member.position}</span>
                        {member.department ? (
                            <span className="text-xs text-gray-400">· {member.department}</span>
                        ) : null}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-gray-500">
                        {member.description}
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-1.5 md:justify-start">
                        {member.expertise.map((skill, i) => (
                            <span key={i} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs text-blue-600/80">
                                {skill}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Team = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const total = teamMembers.length;

    const goTo = useCallback((index) => {
        setActiveIndex(((index % total) + total) % total);
    }, [total]);

    const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
    const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

    // 自动轮播
    useEffect(() => {
        if (paused) return undefined;
        const timer = setInterval(() => {
            setActiveIndex((i) => (i + 1) % total);
        }, 3500);
        return () => clearInterval(timer);
    }, [paused, total]);

    return (
        <div id="team" className="bg-white py-14 md:py-20">
            <div className="mx-auto max-w-5xl px-4 md:px-8">
                {/* 标题 */}
                <div className="mb-10 text-center md:mb-12">
                    <span className="text-sm font-medium tracking-wide text-blue-600">开发实力</span>
                    <h2 className="mb-3 mt-2 text-2xl font-bold text-gray-900 md:text-3xl">
                        精英团队 · 保驾护航
                    </h2>
                    <p className="mx-auto max-w-2xl text-base text-gray-500">
                        汇集国内一线互联网行业精英，筑牢家庭记忆可靠底座
                    </p>
                </div>

                <div
                    className="relative"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                >
                    {/* 左右切换按钮 */}
                    <button
                        type="button"
                        onClick={prev}
                        aria-label="上一位"
                        className="absolute -left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition hover:border-blue-200 hover:text-blue-600 md:-left-4"
                    >
                        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={next}
                        aria-label="下一位"
                        className="absolute -right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition hover:border-blue-200 hover:text-blue-600 md:-right-4"
                    >
                        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    {/* 轮播窗口 */}
                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                        >
                            {teamMembers.map((m) => (
                                <MemberSlide key={m.id} member={m} />
                            ))}
                        </div>
                    </div>

                    {/* 小圆点指示器 */}
                    <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {teamMembers.map((m, i) => {
                            const isActive = i === activeIndex;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => goTo(i)}
                                    aria-label={`第 ${i + 1} 位：${m.name}`}
                                    className={`h-2 rounded-full transition-all duration-300 ${isActive ? 'w-6 bg-blue-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Team;
